/**
 * Feature Flag Service
 *
 * Provides runtime control over MVP feature restrictions.
 * Allows toggling between MVP Mode (restricted features) and Demo Mode (all features).
 *
 * @version 1.0.0
 * @date January 2026
 */

// =============================================================================
// CONSTANTS
// =============================================================================

const BYPASS_MODE_KEY = 'regal_mvp_bypass_mode';
const BYPASS_MODE_EVENT = 'mvpBypassModeChange';

// =============================================================================
// TYPES
// =============================================================================

export interface BypassModeChangeEvent extends CustomEvent {
  detail: {
    enabled: boolean;
    timestamp: number;
  };
}

// =============================================================================
// SERVICE FUNCTIONS
// =============================================================================

/**
 * Check if bypass mode is enabled
 * When bypass mode is enabled, all MVP restrictions are lifted (Demo Mode)
 * When bypass mode is disabled, MVP restrictions are enforced (MVP Mode)
 *
 * @returns true if bypass mode is enabled (Demo Mode), false otherwise (MVP Mode)
 */
export function isBypassModeEnabled(): boolean {
  try {
    const stored = localStorage.getItem(BYPASS_MODE_KEY);
    return stored === 'true';
  } catch {
    // localStorage not available (SSR, private browsing, etc.)
    return false;
  }
}

/**
 * Set bypass mode state
 *
 * @param enabled - true to enable bypass mode (Demo Mode), false to disable (MVP Mode)
 */
export function setBypassMode(enabled: boolean): void {
  try {
    localStorage.setItem(BYPASS_MODE_KEY, String(enabled));

    // Dispatch custom event for components to react to the change
    const event = new CustomEvent(BYPASS_MODE_EVENT, {
      detail: {
        enabled,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);

    // Log for debugging
    console.info(
      `[Feature Flags] ${enabled ? 'Demo Mode' : 'MVP Mode'} activated`,
      { bypassEnabled: enabled }
    );
  } catch (error) {
    console.error('[Feature Flags] Failed to set bypass mode:', error);
  }
}

/**
 * Toggle bypass mode and return the new state
 *
 * @returns The new bypass mode state after toggling
 */
export function toggleBypassMode(): boolean {
  const currentState = isBypassModeEnabled();
  const newState = !currentState;
  setBypassMode(newState);
  return newState;
}

/**
 * Get the current mode label for display
 *
 * @returns 'Demo Mode' if bypass is enabled, 'MVP Mode' otherwise
 */
export function getCurrentModeLabel(): string {
  return isBypassModeEnabled() ? 'Demo Mode' : 'MVP Mode';
}

/**
 * Get the current mode description
 *
 * @returns Description of what the current mode means
 */
export function getCurrentModeDescription(): string {
  if (isBypassModeEnabled()) {
    return 'All features are unlocked. Credit Management, Credit Block, Leads, Contacts, Opportunities, Reports, and advanced admin features are visible.';
  }
  return 'Running in MVP mode with restricted features. Only core CRM functionality and CyberSweep are available.';
}

/**
 * Subscribe to bypass mode changes
 *
 * @param callback - Function to call when bypass mode changes
 * @returns Cleanup function to unsubscribe
 */
export function subscribeToBypassModeChanges(
  callback: (enabled: boolean) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as BypassModeChangeEvent;
    callback(customEvent.detail.enabled);
  };

  window.addEventListener(BYPASS_MODE_EVENT, handler);

  return () => {
    window.removeEventListener(BYPASS_MODE_EVENT, handler);
  };
}

// =============================================================================
// PER-FEATURE FLAGS
// =============================================================================

/**
 * Array credit-pull UI flag. Env-based for v1 — when we add a second keyed
 * flag, promote this to a proper map-backed system.
 */
export function isArrayCreditPullEnabled(): boolean {
  return process.env.REACT_APP_ARRAY_CREDIT_PULL_ENABLED === 'true';
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

const featureFlagService = {
  isBypassModeEnabled,
  setBypassMode,
  toggleBypassMode,
  getCurrentModeLabel,
  getCurrentModeDescription,
  subscribeToBypassModeChanges,
  isArrayCreditPullEnabled,
  BYPASS_MODE_EVENT
};

export default featureFlagService;
